import { fetchContribScore } from "@/shared/api/contribScore";
import { fetchOtherScores } from "@/shared/api/otherScores";
import { getAdminPreviewAsUser, resolveIsAdmin } from "@/shared/config/admin";
import { ACHIEVEMENTS_PUBLIC } from "@/features/achievements/model/flags";
import { ladderValue } from "@/features/achievements/lib/ladders";
import { loadMyAchievements } from "@/features/achievements/lib/useMyAchievements";
import type { AchievementsView } from "@/features/achievements/model/types";
import { mineContent, type MineContent } from "../model/mine";
import type { SphereKey } from "../model/spheres";

/**
 * Баллы человека и уровни для окна «за что эти баллы» — заранее, до нажатия, один раз на
 * страницу (и из кешей: счета, ответ достижений и кусок каталога уже общие для сайта).
 *
 * Источник счёта — тот же, что у карточек сфер экрана «Как получить баллы» (PointsHowTo):
 * вклад — /api/contrib/me (contribScore), остальные четыре — /account/scores воркера
 * (otherScores); поэтому число в окне и «N из M» на карточке совпадают.
 *
 * Окно показывает только то, что уже есть в момент нажатия (peek): запоздавший ответ не
 * вставляет строку в открытое окно и не сдвигает кнопки под пальцем — он достанется
 * следующему нажатию. Не вошёл, нет прав на предпросмотр, ответ не пришёл — окно как раньше.
 *
 * Калитка — та же, что у чипов (usePointsPreview: администратор, не «смотреть как
 * пользователь»), уровни — ещё и та же, что у достижений (useAchievementsPreview); без
 * хуков — окно стоит в корне сайта, и лишних запросов у остальных быть не должно.
 */

type LevelsModule = typeof import("./mineLevels");

type Snapshot = {
    scores: Record<SphereKey, number | null>;
    levels: LevelsModule | null;
    view: AchievementsView | null;
};

let snap: Snapshot | null = null;
let running: Promise<void> | null = null;
let doneAt = 0;
/** Повтор не раньше: счета и так в кешах, а наведение мыши на чип случается часто. */
const FRESH_MS = 15_000;

const read = (key: string): string => {
    try {
        return localStorage.getItem(key) || "";
    } catch {
        return "";
    }
};

/** Предпросмотр баллов (как usePointsPreview, без хука): администратор и не «как пользователь». */
const pointsAllowed = async (): Promise<boolean> => {
    if (getAdminPreviewAsUser()) return false;
    if (process.env.REACT_APP_DEV_MODE === "true") return true;
    const email = read("site_user_email").trim();
    if (!email) return false;
    try {
        return await resolveIsAdmin(email);
    } catch {
        return false;
    }
};

const loadLevels = (): Promise<LevelsModule | null> => import("./mineLevels").catch(() => null);

/**
 * Подгрузить заранее (идемпотентно): счета, кусок уровней и свой ответ достижений.
 * siteLang — язык страницы: на нём спрашивают достижения и строки целей той же страницы.
 */
export const prefetchMyPoints = (siteLang: string): Promise<void> => {
    if (running) return running;
    if (snap && Date.now() - doneAt < FRESH_MS) return Promise.resolve();
    running = (async () => {
        if (!read("site_session_token")) {
            snap = null;
            return;
        }
        const allowed = await pointsAllowed();
        if (!allowed) {
            snap = null;
            return;
        }
        const levelsAllowed = ACHIEVEMENTS_PUBLIC || allowed;
        const [contrib, other, levels, view] = await Promise.all([
            fetchContribScore().catch(() => null),
            fetchOtherScores().catch(() => null),
            levelsAllowed ? loadLevels() : Promise.resolve(null),
            levelsAllowed ? loadMyAchievements(siteLang).catch(() => null) : Promise.resolve(null),
        ]);
        snap = {
            scores: {
                contrib: contrib && contrib.signed_in ? contrib.points : null,
                workouts: other?.workouts ?? null,
                social: other?.social ?? null,
                referrals: other?.referrals ?? null,
                learning: other?.learning ?? null,
            },
            levels,
            view: view ?? null,
        };
        doneAt = Date.now();
    })().finally(() => {
        running = null;
    });
    return running;
};

/**
 * «Ваши баллы» сферы — из того, что уже загружено (без ожидания). null — не вошёл, нет прав,
 * ещё не загрузилось или у сферы нет счёта: окно без этого блока.
 */
export const myPointsFor = (sphere: SphereKey, siteLang: string): MineContent | null => {
    if (!snap) return null;
    const value = snap.scores[sphere];
    if (value == null) return null;
    const level = snap.levels ? snap.levels.mineLevel(sphere, value, snap.view, siteLang) : null;
    // «Тренировки» — недели по плану, как строка профиля и уровень (тот же ответ достижений).
    const weeks = sphere === "workouts" && snap.view ? ladderValue(snap.view, "workouts") : undefined;
    return mineContent(sphere, value, level, siteLang, weeks);
};

/** Папка картинок уровней — когда кусок уровней загружен. */
export const myLevelsImgVersion = (): string | null => snap?.levels?.MINE_IMG_VERSION ?? null;

/** Для тестов. */
export const resetMyPoints = () => {
    snap = null;
    running = null;
    doneAt = 0;
};
